/**
 * Signing in (docs/ops-protocol.md, "Signing in"): the page side of
 * AuthenticationServices' `ASWebAuthenticationSession` and SwiftUI's
 * `WebAuthenticationSession` (Sources/AuthenticationServices). The app sends a
 * `webAuth` request with the provider's authorize URL; the page opens it in a
 * popup window and answers with the URL the window ends at.
 *
 * An app's OAuth redirect is a URL in its own scheme (`icecubesapp://`), which
 * a page cannot receive. The shim swaps it for the page's callback page,
 * `/app/oauth-callback.html?sb_scheme=icecubesapp` (FoundationBridge's
 * WebAuthRedirects.swift), in the authorize URL and in the app's own requests
 * to the provider, so the provider redirects the popup there. That page
 * (public/app/oauth-callback.html) posts its URL to this page, by
 * `postMessage` to its opener and on a `BroadcastChannel` (which still works
 * when a provider's `Cross-Origin-Opener-Policy` cut the popup off from its
 * opener), and closes. The app rebuilds `icecubesapp://?code=…` from it.
 *
 * Browsers only open a popup shortly after a tap, and an app usually registers
 * with the provider first (a network round trip). When the popup is blocked,
 * the page asks the viewer to continue and opens it from that click. Closing
 * the popup, or dismissing that prompt, fails the request with `cancelled: …`,
 * which the app sees as `ASWebAuthenticationSessionError.canceledLogin`.
 */

import type { WebAuthRequestParams } from './protocol';

/** Where the callback page is served, relative to the site's base. */
export const CALLBACK_PATH = 'app/oauth-callback.html';
/** The `type` of the callback page's message. */
export const CALLBACK_MESSAGE = 'sb-web-auth-callback';
/** The `BroadcastChannel` the callback page also posts on. */
export const CALLBACK_CHANNEL = 'sb-web-auth';

/** The value of a `webAuth` `start`: the URL the sign-in window ended at. */
export interface WebAuthResult {
  url: string;
}

/** The parts of a `Window` the session uses (tests substitute them). */
export interface PopupLike {
  readonly closed: boolean;
  close(): void;
}

/** The browser facilities a sign-in uses; tests substitute them. */
export interface WebAuthHost {
  /** Opens `url` in a popup window; null when the browser blocked it. */
  open(url: string): PopupLike | null;
  /** Calls `handler` with each URL the callback page reports; returns a function that stops listening. */
  onCallback(handler: (url: string) => void): () => void;
  /**
   * After a blocked popup: asks the viewer to continue, and opens `url` from
   * their click. Resolves with the popup, or null when they cancel.
   */
  askToOpen?(url: string): Promise<PopupLike | null>;
  /** How often to check whether the viewer closed the popup, in ms (default 500). */
  pollInterval?: number;
}

/** The callback page's URL for a page whose base URL (`import.meta.env.BASE_URL` resolved against the location) is `base`. */
export function callbackPageURL(base: string): string {
  return new URL(CALLBACK_PATH, base).href;
}

/** The URL in a callback page's message, or null when `data` is not one. */
export function callbackURLOf(data: unknown): string | null {
  if (typeof data !== 'object' || data === null) return null;
  const message = data as { type?: unknown; url?: unknown };
  return message.type === CALLBACK_MESSAGE && typeof message.url === 'string' ? message.url : null;
}

interface PendingSignIn {
  popup: PopupLike | null;
  settle: (outcome: { url: string } | { error: string }) => void;
}

/**
 * The page's sign-in sessions: one at a time, as on iOS. A new `start`
 * cancels the one before it.
 */
export class WebAuthSessions {
  private pending: PendingSignIn | null = null;

  constructor(private readonly host: WebAuthHost) {}

  /**
   * Performs one `webAuth` request. `start` must be called within the task
   * that received the request: it opens the popup before its first `await`.
   */
  perform(params: WebAuthRequestParams): Promise<WebAuthResult | Record<string, never>> {
    if (params.action === 'cancel') {
      this.cancel('cancelled: the app cancelled the sign-in');
      return Promise.resolve({});
    }
    if (!params.url || !/^https?:/i.test(params.url)) {
      return Promise.reject(new Error(`unsupported: cannot sign in at ${params.url ?? '(no URL)'}`));
    }
    return this.start(params.url);
  }

  /** Ends the pending sign-in, if any, with `message`, and closes its window. */
  cancel(message: string): void {
    this.pending?.settle({ error: message });
  }

  private start(url: string): Promise<WebAuthResult> {
    this.cancel('cancelled: another sign-in started');
    return new Promise<WebAuthResult>((resolve, reject) => {
      let timer: ReturnType<typeof setInterval> | null = null;
      let closedSeen = false;
      const pending: PendingSignIn = { popup: null, settle: () => {} };
      const stopListening = this.host.onCallback((callback) => pending.settle({ url: callback }));
      pending.settle = (outcome) => {
        if (this.pending !== pending) return;
        this.pending = null;
        stopListening();
        if (timer !== null) clearInterval(timer);
        try {
          if (pending.popup && !pending.popup.closed) pending.popup.close();
        } catch {
          // A window another origin owns may refuse; it is the viewer's then.
        }
        if ('url' in outcome) resolve({ url: outcome.url });
        else reject(new Error(outcome.error));
      };
      this.pending = pending;

      const watch = (popup: PopupLike): void => {
        pending.popup = popup;
        timer = setInterval(() => {
          if (!popup.closed) return;
          // The callback page posts, then closes itself: give its message one
          // more tick to arrive before calling the sign-in cancelled.
          if (!closedSeen) {
            closedSeen = true;
            return;
          }
          pending.settle({ error: 'cancelled: the viewer closed the sign-in window' });
        }, this.host.pollInterval ?? 500);
      };

      // Opened before any await, so a tap's activation still counts.
      const popup = this.host.open(url);
      if (popup) {
        watch(popup);
        return;
      }
      if (!this.host.askToOpen) {
        pending.settle({ error: 'blocked: the browser blocked the sign-in window' });
        return;
      }
      this.host.askToOpen(url).then(
        (opened) => {
          if (this.pending !== pending) {
            opened?.close();
            return;
          }
          if (opened) watch(opened);
          else pending.settle({ error: 'cancelled: the viewer did not continue to the sign-in window' });
        },
        (error: unknown) => pending.settle({ error: `blocked: ${error instanceof Error ? error.message : String(error)}` }),
      );
    });
  }
}

// ---------------------------------------------------------------------------
// The browser
// ---------------------------------------------------------------------------

/** A popup centered on the page, the size of a phone's sign-in sheet. */
function popupFeatures(): string {
  const width = 480;
  const height = 720;
  const left = Math.max(0, Math.round(window.screenX + (window.outerWidth - width) / 2));
  const top = Math.max(0, Math.round(window.screenY + (window.outerHeight - height) / 2));
  return `popup,width=${width},height=${height},left=${left},top=${top}`;
}

/**
 * The prompt shown when the browser blocked the popup: the provider's host and
 * two buttons. Continue opens the popup from the viewer's click.
 */
function promptToOpen(url: string, open: (url: string) => PopupLike | null): Promise<PopupLike | null> {
  let host = url;
  try {
    host = new URL(url).host;
  } catch {
    // Shown as is.
  }
  const overlay = document.createElement('div');
  overlay.className = 'sb-web-auth-prompt';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-label', 'Sign in');
  overlay.style.cssText =
    'position:fixed;inset:0;z-index:2147483647;display:flex;align-items:center;justify-content:center;' +
    'background:rgba(0,0,0,0.4);font:15px/1.4 -apple-system,system-ui,sans-serif;';
  const card = document.createElement('div');
  card.style.cssText =
    'max-width:300px;margin:16px;padding:20px;border-radius:14px;background:Canvas;color:CanvasText;' +
    'box-shadow:0 10px 40px rgba(0,0,0,0.3);text-align:center;';
  const title = document.createElement('p');
  title.style.cssText = 'margin:0 0 6px;font-weight:600;font-size:17px;';
  title.textContent = `Sign in to ${host}`;
  const body = document.createElement('p');
  body.style.cssText = 'margin:0 0 16px;opacity:0.7;';
  body.textContent = 'The sign-in page opens in a new window.';
  const buttons = document.createElement('div');
  buttons.style.cssText = 'display:flex;gap:8px;justify-content:center;';
  const button = (label: string, action: string, primary: boolean): HTMLButtonElement => {
    const element = document.createElement('button');
    element.type = 'button';
    element.textContent = label;
    element.dataset['action'] = action;
    element.style.cssText =
      'flex:1;padding:10px 14px;border-radius:10px;border:0;font:inherit;cursor:pointer;' +
      (primary ? 'background:#0088ff;color:#fff;font-weight:600;' : 'background:rgba(120,120,128,0.2);color:inherit;');
    return element;
  };
  const cancel = button('Cancel', 'cancel', false);
  const proceed = button('Continue', 'continue', true);
  buttons.append(cancel, proceed);
  card.append(title, body, buttons);
  overlay.append(card);
  document.body.append(overlay);
  proceed.focus();
  return new Promise((resolve) => {
    const finish = (popup: PopupLike | null): void => {
      overlay.remove();
      resolve(popup);
    };
    proceed.addEventListener('click', () => finish(open(url)));
    cancel.addEventListener('click', () => finish(null));
  });
}

/** The real browser: `window.open`, the callback page's messages, and the prompt. */
export function browserWebAuthHost(): WebAuthHost | undefined {
  if (typeof window === 'undefined' || typeof window.open !== 'function') return undefined;
  const open = (url: string): PopupLike | null => window.open(url, 'sb-web-auth', popupFeatures());
  const host: WebAuthHost = {
    open,
    onCallback(handler) {
      const onMessage = (event: MessageEvent): void => {
        if (event.origin !== location.origin) return;
        const url = callbackURLOf(event.data);
        if (url) handler(url);
      };
      window.addEventListener('message', onMessage);
      let channel: BroadcastChannel | null = null;
      if (typeof BroadcastChannel === 'function') {
        channel = new BroadcastChannel(CALLBACK_CHANNEL);
        channel.onmessage = (event: MessageEvent) => {
          const url = callbackURLOf(event.data);
          if (url) handler(url);
        };
      }
      return () => {
        window.removeEventListener('message', onMessage);
        channel?.close();
      };
    },
  };
  if (typeof document !== 'undefined') host.askToOpen = (url) => promptToOpen(url, open);
  return host;
}
